import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHero from "@/components/feature/PageHero";
import TrustBar from "@/components/feature/TrustBar";
import ServicesGrid from "@/components/feature/ServicesGrid";
import AIAnswerBlock from "@/components/feature/AIAnswerBlock";
import ProcessSection from "@/components/feature/ProcessSection";
import WhyChooseUs from "@/components/feature/WhyChooseUs";
import ServiceAreas from "@/components/feature/ServiceAreas";
import TestimonialsSection from "@/components/feature/TestimonialsSection";
import FAQSection from "@/components/feature/FAQSection";
import FinalCTA from "@/components/feature/FinalCTA";
import LeadForm from "@/components/feature/LeadForm";
import { services } from "@/mocks/services";
import { locationFAQ } from "@/mocks/faq";
import { useScrollReveal } from "@/hooks/useScrollReveal";

// Unique hero image for Location Page — upscale dealership exterior evening
const HERO_IMAGE =
  "https://public.readdy.ai/ai/img_res/e5f322e37ae25fb086e49ed442ff9f42.jpg";

const locationAIItems = [
  {
    icon: "ri-map-pin-line",
    question: "Where is United Auto Group located?",
    answer:
      "United Auto Group is located at 1024 8th St, Farmington, MN 55024 — in Dakota County, approximately 30 miles south of Minneapolis and 25 miles from Saint Paul. The dealership is easily accessible from Highway 3 and I-35.",
  },
  {
    icon: "ri-time-line",
    question: "What are United Auto Group&apos;s business hours?",
    answer:
      "United Auto Group is open Monday through Friday 9:00 AM to 6:00 PM, and Saturday 9:00 AM to 5:00 PM. Closed Sunday. Walk-ins are always welcome. For faster service, call (952) 769-3208 to schedule your visit.",
  },
  {
    icon: "ri-road-map-line",
    question: "What areas does United Auto Group serve from Farmington, MN?",
    answer:
      "From the Farmington location, United Auto Group serves customers throughout Dakota County, Scott County, and the greater Twin Cities metro. Customers regularly travel from Apple Valley, Lakeville, Burnsville, Eagan, Rosemount, Prior Lake, Hastings, and beyond.",
  },
  {
    icon: "ri-car-line",
    question: "Can I buy a used car in Farmington, MN with bad credit?",
    answer:
      "Yes. United Auto Group in Farmington, MN specializes in working with all credit types. Their finance manager works with multiple lenders to secure financing for customers with good credit, bad credit, no credit history, and first-time buyers.",
  },
];

const nearbyCities = [
  { name: "Apple Valley, MN", distance: "~12 miles" },
  { name: "Lakeville, MN", distance: "~8 miles" },
  { name: "Burnsville, MN", distance: "~15 miles" },
  { name: "Eagan, MN", distance: "~18 miles" },
  { name: "Rosemount, MN", distance: "~8 miles" },
  { name: "Prior Lake, MN", distance: "~14 miles" },
  { name: "Savage, MN", distance: "~16 miles" },
  { name: "Hastings, MN", distance: "~18 miles" },
];

function LocationDetail() {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section
      className="bg-white py-20 md:py-28"
      aria-labelledby="location-detail-heading"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div
          ref={ref}
          className={`flex flex-col lg:flex-row gap-12 items-start transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {/* Left: Info */}
          <div className="lg:w-1/2">
            <div className="inline-flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-gold-DEFAULT" />
              <span className="text-gold-DEFAULT text-xs font-bold tracking-[0.22em] uppercase font-sans">
                VISIT US
              </span>
            </div>
            <h2
              id="location-detail-heading"
              className="font-serif text-charcoal leading-tight mb-5"
              style={{ fontSize: "clamp(1.9rem, 3vw, 2.7rem)", lineHeight: 1.1 }}
            >
              Used Cars for Sale in{" "}
              <em className="not-italic text-gold-DEFAULT">Farmington, MN</em>
            </h2>
            <p className="text-charcoal-soft font-sans text-base leading-relaxed mb-5">
              Conveniently located in the heart of Farmington, Minnesota, United Auto Group is the south Twin Cities metro&apos;s destination for quality pre-owned vehicles. Our lot at 1024 8th St is easily accessible from all Dakota County communities and beyond.
            </p>
            <p className="text-charcoal-soft font-sans text-base leading-relaxed mb-8">
              Whether you are coming from Apple Valley, Lakeville, Burnsville, or making the trip from Minneapolis — our team is ready to give you the most personal, no-pressure vehicle buying experience you have ever had.
            </p>

            {/* Info cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-7">
              <div className="bg-cream-DEFAULT rounded-xl p-5">
                <div className="w-8 h-8 flex items-center justify-center mb-3">
                  <i className="ri-map-pin-fill text-gold-DEFAULT text-xl" />
                </div>
                <p className="font-bold text-charcoal text-sm font-sans mb-1">Address</p>
                <a
                  href="https://maps.app.goo.gl/Vm3ubU5UPaCURLk67"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-charcoal-soft text-sm font-sans hover:text-gold-DEFAULT transition-colors cursor-pointer"
                >
                  1024 8th St<br />Farmington, MN 55024
                </a>
              </div>
              <div className="bg-cream-DEFAULT rounded-xl p-5">
                <div className="w-8 h-8 flex items-center justify-center mb-3">
                  <i className="ri-phone-fill text-gold-DEFAULT text-xl" />
                </div>
                <p className="font-bold text-charcoal text-sm font-sans mb-1">Phone</p>
                <a
                  href="tel:9527693208"
                  className="text-charcoal-soft text-sm font-sans hover:text-gold-DEFAULT transition-colors cursor-pointer whitespace-nowrap"
                  aria-label="Call (952) 769-3208"
                >
                  (952) 769-3208
                </a>
              </div>
              <div className="bg-cream-DEFAULT rounded-xl p-5">
                <div className="w-8 h-8 flex items-center justify-center mb-3">
                  <i className="ri-time-fill text-gold-DEFAULT text-xl" />
                </div>
                <p className="font-bold text-charcoal text-sm font-sans mb-1">Hours</p>
                <p className="text-charcoal-soft text-sm font-sans">
                  Mon–Fri: 9AM–6PM<br />Sat: 9AM–5PM · Sun: Closed
                </p>
              </div>
              <div className="bg-cream-DEFAULT rounded-xl p-5">
                <div className="w-8 h-8 flex items-center justify-center mb-3">
                  <i className="ri-mail-fill text-gold-DEFAULT text-xl" />
                </div>
                <p className="font-bold text-charcoal text-sm font-sans mb-1">Email</p>
                <a
                  href="mailto:info@uagmn.com"
                  className="text-charcoal-soft text-sm font-sans hover:text-gold-DEFAULT transition-colors cursor-pointer whitespace-nowrap"
                >
                  info@uagmn.com
                </a>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="tel:9527693208"
                className="inline-flex items-center justify-center gap-2 bg-gold-DEFAULT text-charcoal font-bold text-sm px-7 py-4 rounded-full hover:bg-gold-light transition-colors cursor-pointer whitespace-nowrap"
                aria-label="Call (952) 769-3208"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill" />
                </div>
                Call Now
              </a>
              <a
                href="https://maps.app.goo.gl/Vm3ubU5UPaCURLk67"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 border-2 border-charcoal/30 text-charcoal font-semibold text-sm px-7 py-4 rounded-full hover:bg-charcoal hover:text-white hover:border-charcoal transition-colors cursor-pointer whitespace-nowrap"
              >
                Get Directions
              </a>
            </div>
          </div>

          {/* Right: Map */}
          <div className="lg:w-1/2">
            <div className="w-full h-[440px] rounded-2xl overflow-hidden border border-cream-dark">
              <iframe
                title="United Auto Group Farmington MN map"
                src="https://maps.google.com/maps?q=United+Auto+Group,+1024+8th+St,+Farmington,+MN+55024&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="bg-charcoal rounded-xl p-4 text-center">
                <div className="font-serif text-gold-DEFAULT text-2xl">30 mi</div>
                <div className="text-white/60 text-xs font-sans mt-1">From Minneapolis</div>
              </div>
              <div className="bg-charcoal rounded-xl p-4 text-center">
                <div className="font-serif text-gold-DEFAULT text-2xl">60+</div>
                <div className="text-white/60 text-xs font-sans mt-1">Vehicles In Stock</div>
              </div>
              <div className="bg-charcoal rounded-xl p-4 text-center">
                <div className="font-serif text-gold-DEFAULT text-2xl">5★</div>
                <div className="text-white/60 text-xs font-sans mt-1">Google Rating</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CityCard({ city, index }: { city: typeof nearbyCities[0]; index: number }) {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={`bg-white rounded-xl p-5 border border-cream-dark transition-all duration-400 hover:-translate-y-0.5 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      <div className="flex items-center gap-2 mb-1">
        <div className="w-3 h-3 flex items-center justify-center">
          <i className="ri-map-pin-fill text-gold-DEFAULT text-xs" />
        </div>
        <p className="font-sans font-bold text-charcoal text-sm whitespace-nowrap">
          {city.name}
        </p>
      </div>
      <p className="text-charcoal-soft text-xs font-sans pl-5">
        {city.distance}
      </p>
    </div>
  );
}

function NearbyCities() {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section
      className="bg-cream-DEFAULT py-16 md:py-20"
      aria-labelledby="nearby-heading"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div
          ref={ref}
          className={`text-center mb-10 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <h2
            id="nearby-heading"
            className="font-serif text-charcoal"
            style={{ fontSize: "clamp(1.7rem, 2.5vw, 2.3rem)" }}
          >
            We Also Serve These Nearby Communities
          </h2>
          <p className="text-charcoal-soft font-sans text-sm mt-3">
            Distance from United Auto Group — 1024 8th St, Farmington, MN 55024
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {nearbyCities.map((city, i) => (
            <CityCard key={city.name} city={city} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function LocationPage() {
  return (
    <div className="font-sans">
      <Navbar />

      {/* 1. Hero */}
      <PageHero
        breadcrumb="Home › Location — Farmington, MN"
        title="Used Cars for Sale in"
        titleAccent="Farmington, Minnesota"
        subtitle="Visit United Auto Group at 1024 8th St, Farmington, MN 55024. Quality pre-owned vehicles, flexible financing for all credit types, and the most trusted team in the south Twin Cities metro."
        backgroundImage={HERO_IMAGE}
        primaryCTA={{ label: "Get Directions", href: "https://maps.app.goo.gl/Vm3ubU5UPaCURLk67" }}
        secondaryCTA={{ label: "Browse Inventory", href: "https://www.uagmn.com/inventory/" }}
        fullScreen={false}
        showPhone
      />

      {/* 2. Trust Bar */}
      <TrustBar />

      {/* 3. Page-Unique: Location Detail + Nearby Cities */}
      <LocationDetail />
      <NearbyCities />

      {/* 4. Services Overview */}
      <ServicesGrid
        title="Quality Pre-Owned Vehicles Available in Farmington, MN"
        subtitle="Browse our complete range of automotive services — all conveniently located at 1024 8th St, Farmington, MN."
        services={services}
        eyebrow="AT OUR FARMINGTON LOCATION"
      />

      {/* 5. AI Answer Block */}
      <AIAnswerBlock
        title="Your Questions About Our Farmington, MN Location"
        subtitle="Everything you need to know before visiting United Auto Group at 1024 8th St, Farmington, MN 55024."
        items={locationAIItems}
      />

      {/* 6. Process */}
      <ProcessSection
        title="Visit Us in Farmington — Here Is What to Expect"
        subtitle="From walking onto our Farmington lot to driving home in your vehicle — here is your step-by-step journey with United Auto Group."
      />

      {/* 7. Why Choose Us */}
      <WhyChooseUs
        title="The Best Place to Buy a Used Car Near Farmington, MN"
        description="Within driving distance of the entire Twin Cities south metro, United Auto Group has earned a reputation as the dealership where customers feel respected, receive honest pricing, and drive away with vehicles they love."
      />

      {/* 8. Service Areas */}
      <ServiceAreas
        title="Serving All of Dakota County & Beyond"
        subtitle="Our Farmington, MN dealership is the hub for quality used car sales across the entire south Twin Cities metropolitan area."
        showMap={false}
      />

      {/* 9. Testimonials */}
      <TestimonialsSection />

      {/* 10. Lead Form */}
      <LeadForm />

      {/* 11. FAQ */}
      <FAQSection
        title="Questions About Our Farmington, MN Location"
        items={locationFAQ}
      />

      {/* 12. Final CTA */}
      <FinalCTA
        title="Come See Us in Farmington, MN Today"
        subtitle="1024 8th St, Farmington, MN 55024 — 30 miles south of Minneapolis. Walk in any time Monday through Saturday. No appointment needed. Our team is ready to help."
        primaryCTA={{ label: "Get Directions Now", href: "https://maps.app.goo.gl/Vm3ubU5UPaCURLk67" }}
      />

      <Footer />
    </div>
  );
}
